/*
<purpose>
  Owns server-side, cursor-paginated archive data for ArchivSeite: rows, the
  next-page cursor, the three per-bucket counts, plus loading/error state.
  Replaces the old capped tm.archivedTickets fetch + client-side filtering.
</purpose>
<dependencies>smarttix.getArchivedPage (services/api.ts)</dependencies>
<constraints>
  - First page fetched on mount and re-fetched whenever bucket or any filter
    changes; cursor resets on every such change (no stale append across buckets).
  - loadMore() is a no-op while a fetch is in flight or when no nextCursor exists.
  - Appends de-dupe by uuid so an over-eager sentinel can't double-insert rows.
  - refresh() is a silent background re-fetch of page 1: never clears rows, never
    flips loading. On page-1 only (pagedBeyond1Ref=false) it replaces rows;
    when paged beyond page 1 it updates counts only (so loadMore'd rows survive).
    On error it keeps existing rows silently. Guards on fetchingRef to avoid overlap.
    Twin of useActivePagination.refresh() — same semantics, archive vocabulary.
</constraints>
*/
import { useState, useEffect, useCallback, useRef } from 'react';
import { TicketListItem, type PillFilterParams, type ArchiveCounts } from '../serverTypes';
import { smarttix } from '../services/api';
import type { ArchiveBucket } from '../services/lifecycleBuckets';

// ArchiveBucket's values ARE the API's own bucket spelling now (part 4c, 2026-09-27) — the
// 'unklassifiziert'-to-'ungeklaert' translation this hook used to own on every call is deleted.

// part 7, 2026-10-11: the pill filters (flag / served search ids; part 7 fix loop, 2026-10-12: hasForm) ride beside the rest —
// declared once in api.ts (PillFilterParams), so this hook forwards them without a second spelling.
export interface ArchivedFilters extends PillFilterParams {
  search?: string;
  primaryAxis?: string | null;
  secondaryAxis?: string[];
  from?: string | null;
  to?: string | null;
  uploadedBy?: string;
  // Classified-alongside ATTRIBUTE filters (wim vocabulary v7, 2026-09-13). Already translated to
  // the wire field spelling by services/ticketAttributes' attributeFilterParams — the page holds the
  // served-name-keyed record, this layer only forwards params.
  sparte?: string[];
  senderKind?: string[];
  // Sender-identity registry (P2, 2026-09-13), Task R2 — see useActivePagination's own field.
  senderOrganisationId?: string[];
}

// The counts shape is declared once, in api.ts, beside the list read that serves it.
export type { ArchiveCounts };

const EMPTY_COUNTS: ArchiveCounts = { erledigt: 0, liegengeblieben: 0, ungeklaert: 0 };

export interface UseArchivedPagination {
  rows: TicketListItem[];
  counts: ArchiveCounts;
  nextCursor: string | null;
  loading: boolean;      // first-page fetch (bucket/filter change)
  loadingMore: boolean;  // append fetch (sentinel)
  error: string | null;
  loadMore: () => void;
  reload: () => void;
  /** Silent background page-1 refetch: never clears rows, never flips loading.
   *  Updates counts always; replaces rows only when on page 1 (not paged beyond).
   *  On error keeps existing rows silently. No-op if a fetch is already in flight. */
  refresh: () => void;
}

export function useArchivedPagination(
  bucket: ArchiveBucket,
  filters: ArchivedFilters,
): UseArchivedPagination {
  const [rows, setRows] = useState<TicketListItem[]>([]);
  const [counts, setCounts] = useState<ArchiveCounts>(EMPTY_COUNTS);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Why: in-flight guard read inside loadMore without re-subscribing the
  // IntersectionObserver effect each render (which would thrash the observer).
  const fetchingRef = useRef(false);
  const cursorRef = useRef<string | null>(null);
  cursorRef.current = nextCursor;
  // Tracks whether the user has scrolled beyond page 1 via loadMore.
  // refresh() uses this to decide whether to replace rows (safe on page 1) or
  // update counts only (rows-replace would discard loadMore'd pages).
  const pagedBeyond1Ref = useRef(false);

  const { search, primaryAxis, secondaryAxis, from, to, uploadedBy, sparte, senderKind, senderOrganisationId, flag, searchPrimary, searchSecondary, hasForm } = filters;
  // secondaryAxis is a fresh array each render; key on its content so the first-page
  // effect doesn't re-fire on every parent render.
  const secondaryAxisKey = (secondaryAxis ?? []).join(',');
  // Same content-keying reason as secondaryAxisKey above — these arrive as fresh arrays each render.
  const sparteKey = (sparte ?? []).join(',');
  const senderKindKey = (senderKind ?? []).join(',');
  const senderOrganisationIdKey = (senderOrganisationId ?? []).join(',');
  const flagKey = (flag ?? []).join(',');
  const searchPrimaryKey = (searchPrimary ?? []).join(',');
  const searchSecondaryKey = (searchSecondary ?? []).join(',');
  // The pill filters arrive ALREADY normalised (searchFaceIds.pillFilterParams: empty lists are
  // undefined, hasForm is true or undefined) and appendPillFilterParams re-checks on the wire, so
  // both fetches forward this one object as-is (final review, part 7, 2026-10-11).
  const pillParams: PillFilterParams = { flag, searchPrimary, searchSecondary, hasForm };

  const fetchPage = useCallback(async (cursor: string | null) => {
    fetchingRef.current = true;
    if (cursor) setLoadingMore(true); else setLoading(true);
    setError(null);
    try {
      const res = await smarttix.getArchivedPage({
        bucket,
        search: search || undefined,
        primaryAxis: primaryAxis || undefined,
        secondaryAxis: secondaryAxis && secondaryAxis.length > 0 ? secondaryAxis : undefined,
        sparte: sparte && sparte.length > 0 ? sparte : undefined,
        senderKind: senderKind && senderKind.length > 0 ? senderKind : undefined,
        senderOrganisationId: senderOrganisationId && senderOrganisationId.length > 0 ? senderOrganisationId : undefined,
        ...pillParams,
        from: from || undefined,
        to: to || undefined,
        uploadedBy: uploadedBy || undefined,
        cursor: cursor || undefined,
      });
      setNextCursor(res.nextCursor);
      if (res.counts) setCounts(res.counts);
      if (cursor) {
        // Append, de-duping by uuid (sentinel can fire twice before state settles).
        pagedBeyond1Ref.current = true;
        setRows(prev => {
          const seen = new Set(prev.map(r => r.uuid));
          return [...prev, ...res.rows.filter(r => !seen.has(r.uuid))];
        });
      } else {
        pagedBeyond1Ref.current = false;
        setRows(res.rows);
      }
    } catch {
      setError('Fehler beim Laden des Archivs');
      if (!cursor) setRows([]);
    } finally {
      fetchingRef.current = false;
      if (cursor) setLoadingMore(false); else setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, search, primaryAxis, secondaryAxisKey, from, to, uploadedBy, sparteKey, senderKindKey, senderOrganisationIdKey, flagKey, searchPrimaryKey, searchSecondaryKey, hasForm]);

  // First page on mount + on every bucket/filter change (cursor reset to null).
  useEffect(() => {
    fetchPage(null);
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (fetchingRef.current) return;
    if (!cursorRef.current) return;
    fetchPage(cursorRef.current);
  }, [fetchPage]);

  const reload = useCallback(() => {
    fetchPage(null);
  }, [fetchPage]);

  // Silent background page-1 refetch — reads filter state from the closure captured
  // in the useCallback dep array (same as fetchPage, so always fresh).
  const refresh = useCallback(async () => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    try {
      const res = await smarttix.getArchivedPage({
        bucket,
        search: search || undefined,
        primaryAxis: primaryAxis || undefined,
        secondaryAxis: secondaryAxis && secondaryAxis.length > 0 ? secondaryAxis : undefined,
        sparte: sparte && sparte.length > 0 ? sparte : undefined,
        senderKind: senderKind && senderKind.length > 0 ? senderKind : undefined,
        senderOrganisationId: senderOrganisationId && senderOrganisationId.length > 0 ? senderOrganisationId : undefined,
        ...pillParams,
        from: from || undefined,
        to: to || undefined,
        uploadedBy: uploadedBy || undefined,
        cursor: undefined,
      });
      setCounts(res.counts ?? EMPTY_COUNTS);
      setNextCursor(res.nextCursor);
      // Only replace rows when not paged beyond page 1 — replacing would drop
      // load-more'd rows that the user has scrolled to.
      if (!pagedBeyond1Ref.current) {
        setRows(res.rows);
      }
    } catch {
      // Keep existing rows + counts; do NOT set blocking error or toggle loading.
    } finally {
      fetchingRef.current = false;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucket, search, primaryAxis, secondaryAxisKey, from, to, uploadedBy, sparteKey, senderKindKey, senderOrganisationIdKey, flagKey, searchPrimaryKey, searchSecondaryKey, hasForm]);

  return { rows, counts, nextCursor, loading, loadingMore, error, loadMore, reload, refresh };
}
